import axios from 'axios';
import schedule from './schedule';

const state = () => ({
  isSelectingPhotos: false,
  photos: [],
  selectedPhotos: [],
  isShowQr: false,
  loading: false,
  contentLoading: false,
  everyonePhotos: [],
  everyonePhotoSchedule: {
    day1: false,
    day2: false,
    day3: false,
    day4: false,
    day5: false,
  },
  selectedDayTabGPC: 'Featured',
  selectedDayTabGPCR: 'Featured',
  selectedDayTabGIC: schedule.state().GICScheduleDates[0],
  selectedMyPhotoTypeTabGPC: 'personal',
  selectedMyPhotoTypeTabGPCR: 'personal',
  viewMore: false,
  failedUrls: [],
  retryTrigger: 0,
});

const getters = {
  isPhotoSelected: state => photo => {
    return state.selectedPhotos.includes(photo);
  },
  isPhotosEmpty: state => {
    return state.photos.length === 0;
  },
};

const mutations = {
  setIsSelectingPhotos(state, value) {
    state.isSelectingPhotos = value;
  },
  setIsShowQr(state, value) {
    state.isShowQr = value;
  },
  setPhotos(state, photos) {
    state.photos = photos;
  },
  setEveryonePhotos(state, photos) {
    state.everyonePhotos = photos;
  },
  addEveryonePhotos(state, photos) {
    if (!photos.length) return;
    state.everyonePhotos = state.everyonePhotos.concat(photos);
  },
  setEveryonePhotoSchedule(state, schedule) {
    state.everyonePhotoSchedule = schedule;
  },
  setSelectedDayTabGPC(state, selectedDayTabGPC) {
    state.selectedDayTabGPC = selectedDayTabGPC;
  },
  setSelectedDayTabGPCR(state, selectedDayTabGPCR) {
    state.selectedDayTabGPCR = selectedDayTabGPCR;
  },
  setSelectedDayTabGIC(state, selectedDayTabGIC) {
    state.selectedDayTabGIC = selectedDayTabGIC;
  },
  setSelectedMyPhotoTypeTabGPC(state, selectedMyPhotoTypeTabGPC) {
    state.selectedMyPhotoTypeTabGPC = selectedMyPhotoTypeTabGPC;
  },
  setSelectedMyPhotoTypeTabGPCR(state, selectedMyPhotoTypeTabGPCR) {
    state.selectedMyPhotoTypeTabGPCR = selectedMyPhotoTypeTabGPCR;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setContentLoading(state, value) {
    state.contentLoading = value;
  },
  addToSelectedPhotos(state, photo) {
    state.selectedPhotos.push(photo);
  },
  removeFromSelectedPhotos(state, photo) {
    state.selectedPhotos = state.selectedPhotos.filter(item => item !== photo);
  },
  resetSelectedPhotos(state) {
    state.selectedPhotos = [];
  },
  setViewMore(state, viewMore) {
    state.viewMore = viewMore;
  },
  setFailedUrls(state, urls) {
    state.failedUrls = urls;
  },
  clearFailedUrls(state) {
    state.failedUrls = [];
  },
  incrementRetryTrigger(state) {
    state.retryTrigger++;
  },
};

const actions = {
  updateIsSelectingPhotos({ commit }, value) {
    commit('setIsSelectingPhotos', value);
  },
  updateIsShowQr({ commit }, value) {
    commit('setIsShowQr', value);
  },
  async fetchPhotos({ commit, dispatch, rootState }, tabName) {
    commit('setLoading', true);
    commit('setContentLoading', true);

    try {
      const accessToken = rootState.user.token;
      const params = {
        tabName: tabName,
      };
      const { data: photos } = await axios.get(process.env.VUE_APP_API_URL + '/photo/all/v3', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: params,
      });
      commit('setPhotos', photos);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch everyone photos.', {
        root: true,
      });
    } finally {
      commit('setLoading', false);
      commit('setContentLoading', false);
    }
  },
  async fetchPhotosEveryone(
    { commit, dispatch, rootState },
    { tabName, startWith, conferenceType = 'Global' },
  ) {
    const params = {
      tabName: tabName,
      conferenceType: conferenceType,
    };

    if (startWith) params.startWith = startWith;
    if (!startWith) commit('setLoading', true);
    // Only show content loading when there are no existing photos (initial open, not day tab switch)
    if (rootState.photos.everyonePhotos.length === 0) commit('setContentLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: photos } = await axios.get(process.env.VUE_APP_API_URL + '/photo/everyone/v2', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: params,
      });
      commit('setViewMore', photos.viewMore);
      if (startWith) {
        commit('addEveryonePhotos', photos.photos);
      } else {
        commit('setEveryonePhotos', photos.photos);
      }
      commit('setEveryonePhotoSchedule', photos.schedules);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch photos.', { root: true });
    } finally {
      commit('setLoading', false);
      commit('setContentLoading', false);
    }
  },
  toggleSelectedPhoto({ getters, commit }, photo) {
    if (getters.isPhotoSelected(photo)) commit('removeFromSelectedPhotos', photo);
    else commit('addToSelectedPhotos', photo);
  },
  addSelectedPhoto({ getters, commit }, photo) {
    if (!getters.isPhotoSelected(photo)) commit('addToSelectedPhotos', photo);
  },
  resetSelectedPhotos({ commit }) {
    commit('resetSelectedPhotos');
  },
  setSelectedDayTabGPC({ commit }, selectedDayTabGPC) {
    commit('setSelectedDayTabGPC', selectedDayTabGPC);
  },
  setSelectedDayTabGPCR({ commit }, selectedDayTabGPCR) {
    commit('setSelectedDayTabGPCR', selectedDayTabGPCR);
  },
  setSelectedDayTabGIC({ commit }, selectedDayTabGIC) {
    commit('setSelectedDayTabGIC', selectedDayTabGIC);
  },
  setSelectedMyPhotoTypeTabGPC({ commit }, selectedMyPhotoTypeTabGPC) {
    commit('setSelectedMyPhotoTypeTabGPC', selectedMyPhotoTypeTabGPC);
  },
  setSelectedMyPhotoTypeTabGPCR({ commit }, selectedMyPhotoTypeTabGPCR) {
    commit('setSelectedMyPhotoTypeTabGPCR', selectedMyPhotoTypeTabGPCR);
  },
  clearFailedDownloads({ commit }) {
    commit('clearFailedUrls');
  },
  triggerRetry({ commit, dispatch }) {
    dispatch('toast/dismiss', null, { root: true });
    commit('incrementRetryTrigger');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
